import React, { useEffect, useMemo, useState } from 'react';
import { getPreliminaryAnswers, updatePreliminaryStatus } from '../api';
import type { PreliminaryAnswerItem } from '../types';

interface PreliminaryRegistryViewProps {
  roleContext?: string;
  onOpenRequest?: (requestCode: string) => void;
}

function statusClass(status: string): string {
  if (status === 'SOURCE_CONFIRMED') return 'ready';
  if (status === 'NEEDS_CONFIRMATION' || status === 'DRAFT_NEEDS_REVIEW') return 'review';
  return 'info';
}

function statusLabel(item: PreliminaryAnswerItem): string {
  return item.status_label || item.status;
}

/** Exact port of template preliminaryView() — owner-uat (= .ps-*). */
export const PreliminaryRegistryView: React.FC<PreliminaryRegistryViewProps> = ({
  roleContext,
  onOpenRequest,
}) => {
  const [items, setItems] = useState<PreliminaryAnswerItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [serviceFilter, setServiceFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await getPreliminaryAnswers({
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        search: search.trim() || undefined,
        role_context: roleContext,
      });
      setItems(res || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchItems();
  }, [statusFilter, search, roleContext]);

  const services = useMemo(() => {
    const map = new Map<string, string>();
    items.forEach((item) => {
      if (item.service_code) {
        map.set(item.service_code, item.service_title || item.service_code);
      }
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [items]);

  const filtered = useMemo(() => {
    return items
      .filter((item) => serviceFilter === 'ALL' || item.service_code === serviceFilter)
      .slice(0, 220);
  }, [items, serviceFilter]);

  const confirmed = items.filter((x) => x.status === 'SOURCE_CONFIRMED').length;
  const needsReview = items.length - confirmed;
  const serviceCount = new Set(items.map((x) => x.service_code).filter(Boolean)).size;

  const handleUpdateStatus = async (id: string, newStatus: string) => {
    try {
      setActionLoadingId(id);
      await updatePreliminaryStatus(id, newStatus, roleContext);
      await fetchItems();
    } catch {
      /* keep list */
    } finally {
      setActionLoadingId(null);
    }
  };

  const openItem = openId ? filtered.find((x) => x.id === openId) ?? null : null;

  const resetLocal = () => {
    setSearch('');
    setStatusFilter('ALL');
    setServiceFilter('ALL');
    void fetchItems();
  };

  return (
    <div className="owner-uat-root">
      <section
        className="owner-uat-route-hero"
        style={{ ['--owner-uat-accent' as string]: '#9e80ff' }}
      >
        <div>
          <div className="owner-uat-route-kicker">Канонический реестр</div>
          <h2>Предварительные ответы</h2>
          <p>
            Реестр владельца сохраняет встроенные записи и их происхождение. Можно искать,
            фильтровать и локально проверять / редактировать существующие записи без новой модели
            контента.
          </p>
        </div>
        <div className="owner-uat-actions">
          <button type="button" className="owner-uat-btn" onClick={resetLocal}>
            Сбросить локальные правки
          </button>
        </div>
      </section>

      <div className="owner-uat-kpis">
        <article className="owner-uat-kpi" style={{ ['--kpi' as string]: '#9e80ff' }}>
          <small>Всего записей</small>
          <strong>{items.length}</strong>
          <span>канонический реестр</span>
        </article>
        <article className="owner-uat-kpi" style={{ ['--kpi' as string]: '#49ddb7' }}>
          <small>Источник подтверждён</small>
          <strong>{confirmed}</strong>
          <span>имеют источник</span>
        </article>
        <article className="owner-uat-kpi" style={{ ['--kpi' as string]: '#e2ad3a' }}>
          <small>Нужна проверка</small>
          <strong>{needsReview}</strong>
          <span>черновик / подтверждение</span>
        </article>
        <article className="owner-uat-kpi" style={{ ['--kpi' as string]: '#41d2ff' }}>
          <small>Услуг</small>
          <strong>{serviceCount}</strong>
          <span>покрытие услуг</span>
        </article>
      </div>

      <section className="owner-uat-panel">
        <header>
          <div>
            <h3>Реестр предварительных ответов</h3>
            <p>Показываются первые 220 записей текущего фильтра.</p>
          </div>
        </header>
        <div className="owner-uat-panel-body">
          <div className="owner-uat-toolbar">
            <input
              className="owner-uat-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Поиск по услуге, вопросу, ответу…"
            />
            <select
              className="owner-uat-select"
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
            >
              <option value="ALL">Все услуги</option>
              {services.map(([code, title]) => (
                <option key={code} value={code}>
                  {code} · {title}
                </option>
              ))}
            </select>
            <select
              className="owner-uat-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="ALL">Все статусы</option>
              <option value="SOURCE_CONFIRMED">Источник подтверждён</option>
              <option value="DRAFT_NEEDS_REVIEW">Нужна проверка</option>
              <option value="NEEDS_CONFIRMATION">Нужно подтвердить</option>
            </select>
          </div>
          <div style={{ height: 10 }} />
          {loading ? <div className="owner-uat-empty">Загрузка предварительных ответов…</div> : null}
          {!loading ? (
            <div className="owner-uat-table-wrap">
              <table className="owner-uat-table">
                <thead>
                  <tr>
                    <th>Услуга</th>
                    <th>Вопрос</th>
                    <th>Ответ</th>
                    <th>Статус</th>
                    <th>Источник</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((item) => (
                    <tr
                      key={item.id}
                      data-open="prelim"
                      style={{ cursor: 'pointer' }}
                      onClick={() => setOpenId(item.id)}
                    >
                      <td>
                        <b>{item.service_code || '—'}</b>
                        <br />
                        {item.service_title || item.direction || '—'}
                      </td>
                      <td>
                        {item.question_label || item.title}
                        <br />

                      </td>
                      <td>
                        {(item.excerpt || '').slice(0, 170)}
                        {(item.excerpt || '').length > 170 ? '…' : ''}
                      </td>
                      <td>
                        <span className={`owner-uat-status ${statusClass(item.status)}`}>
                          {statusLabel(item)}
                        </span>
                      </td>
                      <td>
                        <small>{item.source_ref || item.request_code || '—'}</small>
                      </td>
                    </tr>
                  ))}
                  {!filtered.length ? (
                    <tr>
                      <td colSpan={5}>
                        <div className="owner-uat-empty">По текущему фильтру записей нет.</div>
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </section>

      <div
        className={`owner-uat-drawer-backdrop${openItem ? ' show' : ''}`}
        onClick={() => setOpenId(null)}
      />
      <aside className={`owner-uat-drawer${openItem ? ' open' : ''}`} aria-hidden={!openItem}>
        {openItem ? (
          <>
            <header>
              <div style={{ flex: 1 }}>
                <small className="owner-uat-route-kicker">Запись реестра</small>
                <h3 style={{ margin: '6px 0 0' }}>{openItem.question_label || openItem.title}</h3>
                <p style={{ margin: '4px 0 0', color: '#86a4b3', fontSize: 11 }}>
                  {openItem.service_title || openItem.direction || 'Услуга'}
                </p>
              </div>
              <button type="button" className="owner-uat-btn" onClick={() => setOpenId(null)}>
                Закрыть
              </button>
            </header>
            <div className="owner-uat-drawer-content">
              <div className="owner-uat-detail-grid">

                <div>
                  <small>Статус</small>
                  <strong>{statusLabel(openItem)}</strong>
                </div>
                <div>
                  <small>Источник</small>
                  <strong>{openItem.source_ref || '—'}</strong>
                </div>
                <div>
                  <small>Заявка</small>
                  <strong>{openItem.request_code || '—'}</strong>
                </div>
              </div>
              <p style={{ marginTop: 14, color: '#9fb5c1', lineHeight: 1.55 }}>{openItem.excerpt}</p>
              <div className="owner-uat-actions" style={{ marginTop: 14, justifyContent: 'flex-start' }}>
                {openItem.request_code ? (
                  <button
                    type="button"
                    className="owner-uat-btn"
                    onClick={() => onOpenRequest?.(openItem.request_code!)}
                  >
                    Открыть заявку
                  </button>
                ) : null}
                {openItem.status !== 'SOURCE_CONFIRMED' ? (
                  <button
                    type="button"
                    className="owner-uat-btn primary"
                    disabled={actionLoadingId === openItem.id}
                    onClick={() => void handleUpdateStatus(openItem.id, 'SOURCE_CONFIRMED')}
                  >
                    Подтвердить источник
                  </button>
                ) : null}
                {openItem.status !== 'DRAFT_NEEDS_REVIEW' ? (
                  <button
                    type="button"
                    className="owner-uat-btn"
                    disabled={actionLoadingId === openItem.id}
                    onClick={() => void handleUpdateStatus(openItem.id, 'DRAFT_NEEDS_REVIEW')}
                  >
                    На проверку
                  </button>
                ) : null}
              </div>
            </div>
          </>
        ) : null}
      </aside>
    </div>
  );
};
